import { ApplicationConfig, importProvidersFrom, inject, provideEnvironmentInitializer, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, RouterFeatures, withHashLocation, withInMemoryScrolling } from '@angular/router';

import { routes } from './app.routes';
import { CustomerAppsConfig } from '@customer-apps/config/config.module';
import { environment } from 'environment';
import { StyleguideConfig } from '@styleguide/config/config.module';
import { providePushHandlerFactory } from '@customer-apps/push/push.module';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { ApiVersionInterceptor } from '@customer-apps/http/api-version.interceptor';
import { JWTTokenInterceptor } from '@customer-apps/user/jwt-token.interceptor';
import { LogHandler } from '@customer-apps/logger/logger.service';
import { ConsoleLogHandlerService } from '@customer-apps/logger/console-log-handler.service';
import { BrowserModule } from '@angular/platform-browser';
import { ServiceWorkerModule } from '@angular/service-worker';
import { ErrorRoutingModule } from '@customer-apps/error/error-routing.module';
import { ViewportScroller } from '@angular/common';
import { AppViewportScroller } from '@customer-apps/app-state/viewport-scroller';
import { SentryProviders } from '@customer-apps/sentry/sentry-providers';
import { UserRoutingModule } from '@customer-apps/user/user-routing.module';
import { ApiUserLanguageInterceptor } from '@customer-apps/i18n/api-user-language.interceptor';
import { LanguagePreferenceSyncService } from '@customer-apps/i18n/language-preference-sync.service';
import { provideLabelResolver } from '@customer-apps/i18n/translatable-label-resolver.service';

const serviceWorkerEnabled = environment.production && !window['kgrCapacitor'];

const provideRouterFeatures: RouterFeatures[] = [
  withInMemoryScrolling({
    scrollPositionRestoration: 'enabled',
    anchorScrolling: 'enabled',
  }),
];

if (!!window['kgrCapacitor']) {
  provideRouterFeatures.push(withHashLocation());
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, ...provideRouterFeatures),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    { provide: ViewportScroller, useClass: AppViewportScroller },
    {
      provide: CustomerAppsConfig,
      useValue: environment,
    },
    {
      provide: StyleguideConfig,
      useValue: {
        assetVersion: environment.assetVersion,
      },
    },
    providePushHandlerFactory(),
    importProvidersFrom(
      UserRoutingModule,
      BrowserModule,
      ServiceWorkerModule.register('main-sw.js', {
        enabled: serviceWorkerEnabled,
        registrationStrategy: 'registerImmediately',
      }),
      ErrorRoutingModule,
    ),
    ...SentryProviders,
    { provide: HTTP_INTERCEPTORS, useClass: ApiVersionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ApiUserLanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JWTTokenInterceptor, multi: true },
    { provide: LogHandler, useClass: ConsoleLogHandlerService },
    provideEnvironmentInitializer(() => inject(LanguagePreferenceSyncService)),
    provideLabelResolver(),
  ],
};
